import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage, apiFieldErrors } from "../services/api";
import { fetchProviderSchedule, saveProviderSchedule } from "../services/providerService";
import {
  MAX_PERIODS,
  MIN_PERIODS,
  MONTHLY,
  PERIOD_TYPES,
  computeEndsOn,
  isValidCount,
} from "../services/schedulePeriod";
import { labelFor } from "../services/enumLabels";

const DAY_COUNT = 7;
const DEFAULT_INTERVAL = { starts_at: "09:00", ends_at: "17:00" };
const DEFAULT_BREAK = { starts_at: "13:00", ends_at: "14:00", label: "" };

/** API rows -> one entry per weekday, so every day renders whether worked or not. */
const toWeek = (rows) =>
  Array.from({ length: DAY_COUNT }, (_, day) => {
    const intervals = rows
      .filter((r) => r.day === day)
      .map((r) => ({
        starts_at: r.starts_at,
        ends_at: r.ends_at,
        breaks: (r.breaks ?? []).map((b) => ({
          starts_at: b.starts_at,
          ends_at: b.ends_at,
          label: b.label ?? "",
        })),
      }));

    return {
      day,
      working: intervals.length > 0,
      intervals: intervals.length ? intervals : [{ ...DEFAULT_INTERVAL, breaks: [] }],
    };
  });

/**
 * A period's identity — still the pair of dates, because that is what the
 * rows are keyed by and what the API replaces a slice on. The type and the
 * count ride alongside for the label and the form, not for identity: two
 * rosters cannot share a date range and differ only in how they were phrased.
 */
const periodKeyOf = (row) => `${row.effective_from ?? ""}|${row.effective_until ?? ""}`;

/**
 * The roster with no dates at all: written before periods existed, still
 * applying, and no longer writable. It only appears in the list when a branch
 * actually has one — it is no longer the blank slate a new roster starts from.
 */
const LEGACY = "|";

/** The blank slate: a period nobody has described yet. */
const NEW = "new";

/** The distinct periods present in a branch's rows, the legacy one first. */
const periodsIn = (rows) => {
  const keys = [...new Set(rows.map(periodKeyOf))];
  return keys.sort((a, b) => (a === LEGACY ? -1 : b === LEGACY ? 1 : a.localeCompare(b)));
};

/** The period fields a row was saved with, for re-opening it in the form. */
const periodFormOf = (rows, key) => {
  const row = rows.find((r) => periodKeyOf(r) === key);

  if (!row || !row.period_type) {
    return { type: MONTHLY, startsOn: "", count: 1 };
  }

  return {
    type: row.period_type,
    startsOn: row.starts_on ?? row.effective_from ?? "",
    count: row.periods_count ?? 1,
  };
};

/** Week state -> the flat list the API expects; days off contribute nothing. */
const toRows = (week) =>
  week
    .filter((d) => d.working)
    .flatMap((d) =>
      d.intervals.map((i) => ({
        day: d.day,
        starts_at: i.starts_at,
        ends_at: i.ends_at,
        // A blank label is nothing to store.
        breaks: i.breaks.map((b) => ({
          starts_at: b.starts_at,
          ends_at: b.ends_at,
          label: b.label?.trim() ? b.label.trim() : null,
        })),
      }))
    );

/**
 * BIZ-09 — when a provider works, at one branch.
 *
 * The same replace-the-week shape as a branch's own opening hours, one level
 * down. The branch is picked first because a provider covering two locations
 * has a separate week at each, saved one at a time.
 *
 * Containment inside the branch's opening hours is the API's rule, not this
 * form's: the 422 it returns is rendered as-is, so the two can never disagree.
 */
export default function ProviderSchedule({ providerId, branches }) {
  const { t } = useTranslation();

  const [branchId, setBranchId] = useState(branches[0]?.id ?? "");
  // Every row at this branch, across every period; `week` is the slice being
  // edited. Kept whole so the period list knows what else exists.
  const [rows, setRows] = useState([]);
  const [periodKey, setPeriodKey] = useState(NEW);
  // What the form is describing: a type, a start and a count. The end is
  // never held in state — it is derived, and the server computes the real one.
  const [period, setPeriod] = useState({ type: MONTHLY, startsOn: "", count: 1 });
  const [week, setWeek] = useState(() => toWeek([]));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [saved, setSaved] = useState(false);

  // The provider's branch set can change under this component.
  useEffect(() => {
    if (!branches.some((b) => b.id === branchId)) {
      setBranchId(branches[0]?.id ?? "");
    }
  }, [branches, branchId]);

  const load = useCallback(async () => {
    if (!branchId) return;

    setLoading(true);
    setError("");
    try {
      const loaded = await fetchProviderSchedule(providerId, branchId);
      setRows(loaded);

      // Stay on the period being edited when it survives the reload. There is
      // no always-on roster to fall back to any more, so a branch with none
      // lands on a blank period rather than on somebody else's.
      setPeriodKey((current) => {
        if (current === NEW) return NEW;
        const available = periodsIn(loaded);
        return available.includes(current) ? current : NEW;
      });
    } catch (err) {
      setError(apiErrorMessage(err, t("providers.schedule.loadFailed")));
      setRows([]);
      setPeriodKey(NEW);
    } finally {
      setLoading(false);
    }
  }, [providerId, branchId, t]);

  // The selected period decides both the form's fields and the week shown.
  // A blank period starts an empty week; the legacy roster opens with its
  // hours, so replacing it starts from what it says rather than from nothing.
  useEffect(() => {
    if (periodKey !== NEW) setPeriod(periodFormOf(rows, periodKey));
    setWeek(toWeek(periodKey === NEW ? [] : rows.filter((r) => periodKeyOf(r) === periodKey)));
  }, [rows, periodKey]);

  useEffect(() => {
    load();
  }, [load]);

  const updateDay = (day, changes) =>
    setWeek((w) => w.map((d) => (d.day === day ? { ...d, ...changes } : d)));

  const updateInterval = (day, index, field, value) =>
    setWeek((w) =>
      w.map((d) =>
        d.day === day
          ? {
              ...d,
              intervals: d.intervals.map((i, x) => (x === index ? { ...i, [field]: value } : i)),
            }
          : d
      )
    );

  const addInterval = (day) =>
    setWeek((w) =>
      w.map((d) =>
        d.day === day ? { ...d, intervals: [...d.intervals, { ...DEFAULT_INTERVAL, breaks: [] }] } : d
      )
    );

  const removeInterval = (day, index) =>
    setWeek((w) =>
      w.map((d) =>
        d.day === day && d.intervals.length > 1
          ? { ...d, intervals: d.intervals.filter((_, x) => x !== index) }
          : d
      )
    );

  const updateBreak = (day, intervalIndex, breakIndex, field, value) =>
    setWeek((w) =>
      w.map((d) =>
        d.day === day
          ? {
              ...d,
              intervals: d.intervals.map((i, x) =>
                x === intervalIndex
                  ? {
                      ...i,
                      breaks: i.breaks.map((b, y) =>
                        y === breakIndex ? { ...b, [field]: value } : b
                      ),
                    }
                  : i
              ),
            }
          : d
      )
    );

  const addBreak = (day, intervalIndex) =>
    setWeek((w) =>
      w.map((d) =>
        d.day === day
          ? {
              ...d,
              intervals: d.intervals.map((i, x) =>
                x === intervalIndex ? { ...i, breaks: [...i.breaks, { ...DEFAULT_BREAK }] } : i
              ),
            }
          : d
      )
    );

  const removeBreak = (day, intervalIndex, breakIndex) =>
    setWeek((w) =>
      w.map((d) =>
        d.day === day
          ? {
              ...d,
              intervals: d.intervals.map((i, x) =>
                x === intervalIndex
                  ? { ...i, breaks: i.breaks.filter((_, y) => y !== breakIndex) }
                  : i
              ),
            }
          : d
      )
    );

  const save = async () => {
    setSaving(true);
    setError("");
    setFieldErrors({});
    setSaved(false);

    try {
      // The API answers with the branch's whole schedule, every period of it,
      // so the period list stays right after a save that created a new one.
      const saved = await saveProviderSchedule(providerId, branchId, toRows(week), {
        ...period,
        // Editing the legacy roster means replacing it: it has no end date and
        // cannot be written back as it is. Anything else leaves it alone.
        replacesLegacy: periodKey === LEGACY,
      });
      setRows(saved);
      // Onto the period that now exists, keyed the way the rows are. The end
      // is the server's; recomputing it here would be trusting the preview.
      const stored = saved.find(
        (r) => r.starts_on === period.startsOn && r.period_type === period.type
      );
      setPeriodKey(stored ? periodKeyOf(stored) : NEW);
      setSaved(true);
    } catch (err) {
      setFieldErrors(apiFieldErrors(err));
      setError(apiErrorMessage(err, t("providers.schedule.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  /** The API keys errors by position — schedule.0.starts_at and the like. */
  const rowError = (day, index) => {
    // Rows are sent in weekday order, so the flat index is the count of every
    // working interval before this one.
    let flat = 0;
    for (const d of week) {
      if (!d.working) continue;
      for (let x = 0; x < d.intervals.length; x += 1) {
        if (d.day === day && x === index) {
          return (
            fieldErrors[`schedule.${flat}.starts_at`]?.[0] ??
            fieldErrors[`schedule.${flat}.ends_at`]?.[0] ??
            fieldErrors[`schedule.${flat}.day`]?.[0]
          );
        }
        flat += 1;
      }
    }
    return undefined;
  };

  /**
   * How a period reads in the select.
   *
   * A roster with no dates is the legacy one and is named as such rather than
   * "Always" — the word is gone, and calling it that would suggest it is
   * still something somebody can choose.
   */
  const periodLabel = (key) => {
    if (key === NEW) return t("providers.schedule.periodNew");
    if (key === LEGACY) return t("providers.schedule.periodLegacy");

    const row = rows.find((r) => periodKeyOf(r) === key);
    const [from, until] = key.split("|");

    if (row?.period_type && row?.periods_count) {
      return t("providers.schedule.periodNamed", {
        count: row.periods_count,
        type: labelFor("schedule_type", row.period_type),
        from,
        until,
      });
    }

    return t("providers.schedule.periodRange", { from, until });
  };

  const periodKeys = periodsIn(rows);
  const editingLegacy = periodKey === LEGACY;
  const endsOn = computeEndsOn(period.type, period.startsOn, period.count);
  // The server would refuse these anyway; refusing them here saves a round
  // trip and says so next to the field rather than at the top of the page.
  const periodReady = PERIOD_TYPES.includes(period.type)
    && Boolean(period.startsOn)
    && isValidCount(period.count);

  if (branches.length === 0) {
    return (
      <div className="card p-5">
        <p className="text-sm text-ink-2">{t("providers.schedule.noBranches")}</p>
      </div>
    );
  }

  return (
    <div className="card p-5">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold">{t("providers.schedule.title")}</h2>
          <p className="text-sm text-ink-2">{t("providers.schedule.subtitle")}</p>
        </div>

        <div>
          <label className="field-label" htmlFor="schedule-branch">
            {t("providers.schedule.branch")}
          </label>
          <select
            id="schedule-branch"
            className="field-input"
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
          >
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Which roster is being edited, and how long it runs. A branch may
          hold several — a summer one and a winter one — and switching here
          swaps the grid below. The end date is never typed: it is worked out
          from the type, the start and the count, so a period that ends before
          it begins cannot be described. */}
      <div className="mb-4 rounded-lg border border-line bg-raised p-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="field-label" htmlFor="schedule-period">
              {t("providers.schedule.period")}
            </label>
            <select
              id="schedule-period"
              className="field-input"
              value={periodKey}
              onChange={(e) => setPeriodKey(e.target.value)}
            >
              <option value={NEW}>{periodLabel(NEW)}</option>
              {periodKeys.map((key) => (
                <option key={key} value={key}>
                  {periodLabel(key)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="field-label" htmlFor="schedule-period-type">
              {t("providers.schedule.periodType")}
            </label>
            <select
              id="schedule-period-type"
              className="field-input"
              value={period.type}
              onChange={(e) => setPeriod((p) => ({ ...p, type: e.target.value }))}
            >
              {PERIOD_TYPES.map((type) => (
                <option key={type} value={type}>
                  {labelFor("schedule_type", type)}
                </option>
              ))}
            </select>
            {fieldErrors.period_type && (
              <p className="field-error">{fieldErrors.period_type[0]}</p>
            )}
          </div>

          <div>
            <label className="field-label" htmlFor="schedule-period-start">
              {t("providers.schedule.periodStart")}
            </label>
            <input
              id="schedule-period-start"
              type="date"
              className="field-input"
              value={period.startsOn}
              onChange={(e) => setPeriod((p) => ({ ...p, startsOn: e.target.value }))}
            />
            {fieldErrors.starts_on && <p className="field-error">{fieldErrors.starts_on[0]}</p>}
          </div>

          <div>
            <label className="field-label" htmlFor="schedule-period-count">
              {t("providers.schedule.periodCount")}
            </label>
            <input
              id="schedule-period-count"
              type="number"
              inputMode="numeric"
              min={MIN_PERIODS}
              max={MAX_PERIODS}
              step={1}
              className="field-input w-24"
              value={period.count}
              onChange={(e) => setPeriod((p) => ({ ...p, count: e.target.value }))}
            />
            {fieldErrors.periods_count && (
              <p className="field-error">{fieldErrors.periods_count[0]}</p>
            )}
          </div>

          {/* Read-only, and the server recomputes it on save — this is the
              preview, not the value. `dir="ltr"` because a date stays
              left-to-right inside an Arabic sentence. */}
          <div>
            <span className="field-label">{t("providers.schedule.periodEnds")}</span>
            <p
              className="field-input flex items-center bg-surface text-ink-2"
              dir="ltr"
              aria-live="polite"
            >
              {endsOn ?? "—"}
            </p>
          </div>
        </div>

        <p className="mt-2 text-xs text-ink-2">
          {endsOn
            ? t("providers.schedule.periodPreview", {
                count: period.count,
                type: labelFor("schedule_type", period.type),
                from: period.startsOn,
                until: endsOn,
              })
            : t("providers.schedule.periodHint")}
        </p>

        {editingLegacy && (
          <p className="mt-2 rounded-md border border-warning-line bg-warning-wash px-3 py-2 text-xs text-warning-text">
            {t("providers.schedule.legacyNotice")}
          </p>
        )}
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
          {error}
        </div>
      )}

      {saved && (
        <div className="mb-4 rounded-lg border border-accent-line bg-accent-wash px-3 py-2 text-sm text-accent-text">
          {t("providers.schedule.saved")}
        </div>
      )}

      {loading ? (
        <p className="py-8 text-center text-sm text-ink-2">{t("common.loading")}</p>
      ) : (
        <div className="space-y-3">
          {week.map((day) => (
            <div key={day.day} className="rounded-lg border border-line p-3">
              <div className="flex items-center justify-between">
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    checked={day.working}
                    onChange={(e) => updateDay(day.day, { working: e.target.checked })}
                  />
                  <span className="text-sm font-medium">{t(`providers.days.${day.day}`)}</span>
                </label>

                {day.working && (
                  <button
                    type="button"
                    className="text-xs font-medium text-accent-text hover:underline"
                    onClick={() => addInterval(day.day)}
                  >
                    {t("providers.schedule.addInterval")}
                  </button>
                )}
              </div>

              {!day.working ? (
                <p className="mt-1 text-xs text-ink-2">{t("providers.schedule.dayOff")}</p>
              ) : (
                <div className="mt-3 space-y-3">
                  {day.intervals.map((interval, index) => (
                    <div key={index} className="rounded-md bg-raised p-3">
                      <div className="flex flex-wrap items-end gap-2">
                        <div>
                          <label className="field-label">{t("providers.schedule.from")}</label>
                          <input
                            type="time"
                            className="field-input"
                            value={interval.starts_at}
                            onChange={(e) =>
                              updateInterval(day.day, index, "starts_at", e.target.value)
                            }
                          />
                        </div>
                        <div>
                          <label className="field-label">{t("providers.schedule.to")}</label>
                          <input
                            type="time"
                            className="field-input"
                            value={interval.ends_at}
                            onChange={(e) =>
                              updateInterval(day.day, index, "ends_at", e.target.value)
                            }
                          />
                        </div>

                        <button
                          type="button"
                          className="text-xs font-medium text-accent-text hover:underline"
                          onClick={() => addBreak(day.day, index)}
                        >
                          {t("providers.schedule.addBreak")}
                        </button>

                        {day.intervals.length > 1 && (
                          <button
                            type="button"
                            className="text-xs font-medium text-danger-text hover:underline"
                            onClick={() => removeInterval(day.day, index)}
                          >
                            {t("common.remove")}
                          </button>
                        )}
                      </div>

                      {rowError(day.day, index) && (
                        <p className="field-error">{rowError(day.day, index)}</p>
                      )}

                      {interval.breaks.length > 0 && (
                        <div className="mt-3 space-y-2 border-t border-line pt-3">
                          <p className="text-xs font-medium text-ink-2">
                            {t("providers.schedule.breaks")}
                          </p>
                          {interval.breaks.map((brk, breakIndex) => (
                            <div key={breakIndex} className="flex flex-wrap items-end gap-2">
                              <div>
                                <label className="field-label">
                                  {t("providers.schedule.from")}
                                </label>
                                <input
                                  type="time"
                                  className="field-input"
                                  value={brk.starts_at}
                                  onChange={(e) =>
                                    updateBreak(
                                      day.day,
                                      index,
                                      breakIndex,
                                      "starts_at",
                                      e.target.value
                                    )
                                  }
                                />
                              </div>
                              <div>
                                <label className="field-label">{t("providers.schedule.to")}</label>
                                <input
                                  type="time"
                                  className="field-input"
                                  value={brk.ends_at}
                                  onChange={(e) =>
                                    updateBreak(
                                      day.day,
                                      index,
                                      breakIndex,
                                      "ends_at",
                                      e.target.value
                                    )
                                  }
                                />
                              </div>
                              <div className="min-w-[120px] flex-1">
                                <label className="field-label">
                                  {t("providers.schedule.breakLabel")}
                                </label>
                                <input
                                  className="field-input"
                                  placeholder={t("providers.schedule.breakLabelPlaceholder")}
                                  value={brk.label}
                                  onChange={(e) =>
                                    updateBreak(day.day, index, breakIndex, "label", e.target.value)
                                  }
                                />
                              </div>
                              <button
                                type="button"
                                className="text-xs font-medium text-danger-text hover:underline"
                                onClick={() => removeBreak(day.day, index, breakIndex)}
                              >
                                {t("common.remove")}
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn-secondary" onClick={load} disabled={saving}>
              {t("common.cancel")}
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={save}
              disabled={saving || !periodReady}
              title={periodReady ? undefined : t("providers.schedule.periodIncomplete")}
            >
              {saving ? t("common.saving") : t("common.save")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
